import React, { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import Home from './pages/Home';

import { LoginPage } from './pages/auth/LoginPage';
import { SignupPage } from './pages/auth/SignupPage';
import { PasswordResetPage } from './pages/auth/PasswordResetPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { EmailVerificationPage } from './pages/auth/EmailVerificationPage';
import { ReferralCodePage } from './pages/auth/ReferralCodePage';

import { HelpPage } from './pages/service/HelpPage';
import { PrivacyPolicy } from './pages/service/PrivacyPolicy';
import { TermsOfService } from './pages/service/TermsOfService';

import { PricingPage } from './pages/PricingPage';
import { AdminDashboard } from './pages/AdminDashboard';

import { StripeSuccessPage } from './pages/StripeSuccessPage';
import { StripeCancelPage } from './pages/StripeCancelPage';

import { LegalInformation } from './pages/LegalInformation';
import { MyPage } from './pages/MyPage';

import { PrivateRoute } from './components/auth/PrivateRoute';
import  Loading  from './components/common/Loading';
import { showGlobalLoading, hideGlobalLoading } from './hooks/useLoading';

import { useAuthStore } from './stores/authStore';
import { useProductStore } from './stores/productStore';
import { useFolderStore } from './stores/folderStore';
import { useAiCreditsStore } from './stores/aiCreditsStore';
import { useTemplateStore } from './stores/templateStore';
import { useDescriptionTemplateStore } from './stores/descriptionTemplateStore';
import { supabase } from './utils/supabase';



export function App() {
  const { isAuthenticated, user, initializeAuth } = useAuthStore();
  const loadingRef = useRef(false);

  const loadUserData = async () => {
    if (loadingRef.current) {
      console.log('[loadUserData] Skipping duplicate call');
      return;
    }

    try {
      loadingRef.current = true;
      showGlobalLoading();

      await Promise.all([
        useAiCreditsStore.getState().loadCredits(),
        useDescriptionTemplateStore.getState().loadTemplates(),
        useTemplateStore.getState().loadTemplates(),
        useFolderStore.getState().loadFolders(),
        useProductStore.getState().loadProducts(),
      ]);

      console.log('[loadUserData] All data loaded');
    } catch (err) {
      console.error('[loadUserData] Error:', err);
    } finally {
      hideGlobalLoading();
      loadingRef.current = false;
    }
  };

  const handleStripeReturn = async () => {
    const url = new URL(window.location.href);
    const sessionId = url.searchParams.get('session_id');
    if (!sessionId) return;

    try {
      await initializeAuth();
      await useAiCreditsStore.getState().loadCredits();
    } catch (err) {
      console.error('[Stripe] Failed to refresh after Stripe:', err);
    }
  };

  useEffect(() => {
    initializeAuth(); // Handles session check, validation, user loading
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      loadUserData();
      handleStripeReturn();
    }
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    const visibilityHandler = async () => {
      if (document.visibilityState === 'visible') {
        try {
          const { data, error } = await supabase.auth.getSession();
          if (error) return console.error('[VisibilityCheck] Error:', error);
          if (!data.session) return console.log('[VisibilityCheck] No active session');

          await useAiCreditsStore.getState().loadCredits();
        } catch (err) {
          console.error('[VisibilityCheck] Exception:', err);
        }
      }
    };

    document.addEventListener('visibilitychange', visibilityHandler);
    return () => document.removeEventListener('visibilitychange', visibilityHandler);
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<PasswordResetPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<EmailVerificationPage />} />
        <Route path="/stripe-success" element={<StripeSuccessPage />} />
        <Route path="/stripe-cancel" element={<StripeCancelPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/referral-code" element={<ReferralCodePage />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/legal" element={<LegalInformation />} />

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Home /> {/* /Home is for products */}
            </PrivateRoute>
          }
        />

        <Route
          path="/account"
          element={
            <PrivateRoute>
              <MyPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/pricing"
          element={
            <PrivateRoute>
                <PricingPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <PrivateRoute requiredRole="admin">
              <AdminDashboard />
            </PrivateRoute>
          }
        />

        <Route
          path="/help"
          element={
            <PrivateRoute>
              <HelpPage />
            </PrivateRoute>
          }
        />

        {/* Catch-All */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Loading />
    </BrowserRouter>
  );
}
